import React, { useState } from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import Link from 'next/link';
import { utils, Button } from '@makeship/core';
import { useStore } from '../../../store';

import Modal from '..';
import { H5, S2, P2, Overline } from '../../Typography';

import { shareModalTheme } from '../../../styles/themes';
import { sendShareBadgeEvent } from '../../../utils/analytics';
import config from '../../../../config.json';

const { routes } = config;

const ModalContent = styled.div`
  width: 100%;
  padding: 48px 8px 16px 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const CancelIcon = styled.div`
  position: absolute;
  right: 16px;
  top: 16px;
  cursor: pointer;
`;

const ImageBorder = styled.div<{ IsSharingBadge: boolean }>`
  ${({ IsSharingBadge, theme }) => !IsSharingBadge && `border: 1px solid ${theme.colors.neutral2};border-radius: 8px;`};
  margin-bottom: 12px;
`;

const ImageWrapper = styled.div<{ IsSharingBadge: boolean }>`
  width: ${({ IsSharingBadge }) => (IsSharingBadge ? '278px' : '278px')};
  height: ${({ IsSharingBadge }) => (IsSharingBadge ? '278px' : '278px')};
`;

const SocialWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
`;

const SocialItemWrapper = styled.div`
  text-align: center;
  width: 75px;
`;

const SocialLink = styled.a``;

const SocialImageWrapper = styled.div`
  cursor: pointer;
`;

const SocialTitle = styled(Overline)<{ linkCopied?: boolean }>`
  text-align: center;
  color: ${({ theme, linkCopied }) => (!linkCopied ? theme.colors.neutral7 : theme.colors.success)};
`;

const UpdateSettingTitle = styled(H5)`
  margin-bottom: 16px;
  text-align: center;
`;

const UpdateSettingText = styled(P2)`
  margin-bottom: 16px;
  text-align: center;
`;

type SocialType = {
  title: string;
  icon: string;
  alt: string;
  link: string;
};

type ShareModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  badge?: Badge;
  username?: string;
  hidden?: boolean;
};

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, closeModal, badge, username, hidden }: ShareModalProps) => {
  const { state } = useStore();
  const [linkCopied, setLinkCopied] = useState(false);

  const socialLinks: SocialType[] = [
    {
      title: 'Twitter',
      icon: '/assets/icons/twitter-icon2.svg',
      alt: 'twitter icon',
      link: `https://twitter.com/intent/tweet?url=${process.env.NEXT_PUBLIC_DOMAIN}/badges/${
        badge
          ? `${badge.badgeId}?s=${badge.position ? '1' : '0'}${
              username === state.user?.storefrontCustomer?.username
                ? `&text=I%20collected%20my%20${badge.product.title}%20Badge%20from%20@makeship%20on%20${utils.getDate(
                    badge.createdOn,
                  )}.%20Own%20a%20Makeship%20Product?%20Sign%20up%20and%20claim%20yours!`
                : ''
            }`
          : `user/${encodeURIComponent(username || '')}`
      }`,
    },
    {
      title: 'Facebook',
      icon: '/assets/icons/facebook-icon2.svg',
      alt: 'facebook icon',
      link: `https://www.facebook.com/sharer/sharer.php?u=${process.env.NEXT_PUBLIC_DOMAIN}/badges/${
        badge ? `${badge.badgeId}?s=${badge.position ? '1' : '0'}` : `user/${encodeURIComponent(username || '')}`
      }`,
    },
  ];

  const onCopyLink = (str: string) => {
    navigator.clipboard.writeText(str);
    setLinkCopied(true);
    setTimeout(() => {
      setLinkCopied(false);
    }, 3000);
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={closeModal} theme={shareModalTheme}>
      <ModalContent>
        <CancelIcon onClick={closeModal}>
          <Image src="/assets/icons/cancel-icon.svg" width="24px" height="24px" quality={90} />
        </CancelIcon>
        {!hidden ? (
          <>
            <ImageBorder IsSharingBadge={!badge}>
              <ImageWrapper IsSharingBadge={!badge}>
                <Image
                  src={badge ? badge.badgeFrontUrl : '/assets/badges/makeship-badges.png'}
                  layout="responsive"
                  width={!badge ? '1080px' : '278px'}
                  height={!badge ? '1080px' : '278px'}
                  alt={!badge ? 'makeship badges preview image' : 'badge image'}
                  unoptimized
                />
              </ImageWrapper>
            </ImageBorder>
            <S2>
              Share {username === state.user?.storefrontCustomer?.username ? 'Your' : ''} {!badge ? 'Library' : 'Badge'}
            </S2>
            <SocialWrapper>
              {socialLinks.map((social) => (
                <SocialItemWrapper>
                  <SocialLink
                    key={social.link}
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      sendShareBadgeEvent(social.title);
                    }}
                  >
                    <Image src={social.icon} width={48} height={48} alt={social.alt} quality={90} />
                  </SocialLink>
                  <SocialTitle>{social.title}</SocialTitle>
                </SocialItemWrapper>
              ))}
              <SocialItemWrapper>
                <SocialImageWrapper
                  onClick={() =>
                    onCopyLink(
                      `${process.env.NEXT_PUBLIC_DOMAIN}/badges/${
                        badge ? badge.badgeId : `user/${encodeURIComponent(username || '')}`
                      }`,
                    )
                  }
                >
                  <Image
                    src="/assets/icons/copy-link-icon.svg"
                    width={48}
                    height={48}
                    alt="copy link icon"
                    quality={90}
                  />
                </SocialImageWrapper>
                <SocialTitle linkCopied={linkCopied}>{!linkCopied ? 'Copy Link' : 'Link Copied!'}</SocialTitle>
              </SocialItemWrapper>
            </SocialWrapper>
          </>
        ) : (
          <>
            <UpdateSettingTitle>Update Settings to Share</UpdateSettingTitle>
            <UpdateSettingText>To share your Badge Library, update your Username security settings.</UpdateSettingText>
            <Link href={routes.settings} passHref>
              <Button.Primary>Update Now</Button.Primary>
            </Link>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ShareModal;
